// src/utils/iconRenderer.js
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

// Add all icon sets to the library
library.add(fas, far, fab);

/**
 * Dynamic icon renderer that can render any FontAwesome icon
 * @param {string} iconName - The icon name (e.g., 'fa-fire', 'fas fa-trophy', 'fire')
 * @param {object} props - Additional props to pass to the icon
 * @returns {JSX.Element} - FontAwesome icon component
 */
export const DynamicIcon = ({ iconName, ...props }) => {
  if (!iconName) {
    console.warn('DynamicIcon: No iconName provided, using default trophy icon');
    return <FontAwesomeIcon icon={['fas', 'trophy']} {...props} />; // Default fallback
  }

  // Parse different icon name formats
  let prefix = 'fas'; // default to solid
  let iconId = iconName.toLowerCase().trim();

  // Handle formats like "fas fa-fire" or "fa-fire"
  if (iconId.includes(' ')) {
    const parts = iconId.split(' ').filter(part => part.length > 0);
    if (parts.length >= 2) {
      const prefixPart = parts[0];
      // Map different prefix formats
      if (prefixPart === 'fas' || prefixPart === 'fa-solid') prefix = 'fas';
      else if (prefixPart === 'far' || prefixPart === 'fa-regular') prefix = 'far';
      else if (prefixPart === 'fab' || prefixPart === 'fa-brands') prefix = 'fab';
      else if (prefixPart === 'fa') prefix = 'fas'; // Default fa to solid
      
      iconId = parts[parts.length - 1].replace('fa-', '');
    }
  } else {
    // Handle formats like "fa-fire" or just "fire"
    iconId = iconId.replace('fa-', '');
  }

  // Handle prefix in the icon name itself (like "fas-fire")
  if (iconId.includes('-') && (iconId.startsWith('fas') || iconId.startsWith('far') || iconId.startsWith('fab'))) {
    const parts = iconId.split('-');
    prefix = parts[0];
    iconId = parts.slice(1).join('-');
  }

  // Convert kebab-case to the icon identifier expected by FontAwesome
  const iconIdentifier = iconId.toLowerCase();

  console.log(`DynamicIcon: Rendering ${prefix} ${iconIdentifier} from input "${iconName}"`);

  try {
    return <FontAwesomeIcon icon={[prefix, iconIdentifier]} {...props} />;
  } catch (error) {
    console.warn(`DynamicIcon: Icon not found: ${prefix} ${iconIdentifier} (from "${iconName}"), falling back to default trophy`);
    return <FontAwesomeIcon icon={['fas', 'trophy']} {...props} />; // Fallback
  }
};

/**
 * Get all available FontAwesome icons as an object
 * Useful for debugging or listing available icons
 */
export const getAvailableIcons = () => {
  return {
    solid: Object.keys(fas),
    regular: Object.keys(far),
    brands: Object.keys(fab)
  };
};

/**
 * Check if an icon exists in the library
 * @param {string} iconName - The icon name to check
 * @param {string} prefix - The icon prefix (fas, far, fab)
 * @returns {boolean} - Whether the icon exists
 */
export const iconExists = (iconName, prefix = 'fas') => {
  try {
    const iconId = iconName.toLowerCase().replace('fa-', '');
    
    // Convert to camelCase for checking in the library
    const camelCaseId = `fa${iconId.charAt(0).toUpperCase() + iconId.slice(1).replace(/-(.)/g, (_, char) => char.toUpperCase())}`;
    
    switch (prefix) {
      case 'fas':
        return fas[camelCaseId] !== undefined;
      case 'far':
        return far[camelCaseId] !== undefined;
      case 'fab':
        return fab[camelCaseId] !== undefined;
      default:
        return false;
    }
  } catch {
    return false;
  }
};

/**
 * Validate and normalize icon name
 * @param {string} iconName - Raw icon name from backend
 * @returns {object} - {prefix, iconId, isValid}
 */
export const parseIconName = (iconName) => {
  if (!iconName) {
    return { prefix: 'fas', iconId: 'trophy', isValid: false };
  }

  let prefix = 'fas';
  let iconId = iconName.toLowerCase().trim();

  if (iconId.includes(' ')) {
    const parts = iconId.split(' ').filter(part => part.length > 0);
    if (parts.length >= 2) {
      const prefixPart = parts[0];
      if (prefixPart === 'fas' || prefixPart === 'fa-solid') prefix = 'fas';
      else if (prefixPart === 'far' || prefixPart === 'fa-regular') prefix = 'far';
      else if (prefixPart === 'fab' || prefixPart === 'fa-brands') prefix = 'fab';
      
      iconId = parts[parts.length - 1].replace('fa-', '');
    }
  } else {
    iconId = iconId.replace('fa-', '');
  }

  const isValid = iconExists(iconId, prefix);
  
  return { prefix, iconId, isValid };
};

export default DynamicIcon;
